import { http } from "./config";
import authHeader from "./auth-header";
import {
  normalizeAgreement,
  normalizeClaim,
  normalizeInstallment,
} from "./fluxoCobranca.service";

const baseUrl = "/fluxo-vendas";

// IDs das etapas cadastradas em tbl_fluxo_etapas (fluxo = 'vendas')
export const ETAPAS_VENDAS = Object.freeze({
  VENDAS_VALIDADAS: 34,
  INAD_1_PARCELA: 35,
  INAD_2_PARCELA: 36,
  INAD_3_PARCELA: 37,
  ADIMPLENTES: 38,
  CANCELADO: 39,
});

const COLUNAS_VISUAL = Object.freeze({
  [ETAPAS_VENDAS.VENDAS_VALIDADAS]: {
    description: "Contratos ativos, sem mensalidades lançadas",
    icon: "mdi-check-decagram",
  },
  [ETAPAS_VENDAS.INAD_1_PARCELA]: {
    description: "1 parcela em atraso",
    icon: "mdi-alert-outline",
  },
  [ETAPAS_VENDAS.INAD_2_PARCELA]: {
    description: "2 parcelas em atraso",
    icon: "mdi-alert",
  },
  [ETAPAS_VENDAS.INAD_3_PARCELA]: {
    description: "3 ou mais parcelas em atraso",
    icon: "mdi-alert-octagon",
  },
  [ETAPAS_VENDAS.ADIMPLENTES]: {
    description: "Ativos, com mensalidades lançadas e sem atraso",
    icon: "mdi-cash-check",
  },
  [ETAPAS_VENDAS.CANCELADO]: {
    description: "Contrato cancelado",
    icon: "mdi-cancel",
  },
});

function normalizeColumn(column) {
  const id = Number(column.id);
  const visual = COLUNAS_VISUAL[id] || {};
  return {
    ...column,
    id,
    title: column.title || column.nome,
    description: visual.description || column.tipo,
    color: column.color || column.cor || "#64748b",
    icon: visual.icon || "mdi-view-column",
    order: Number(column.order || column.ordem || 0),
    type: column.type || column.tipo,
  };
}

const DATA_NULA_SONAX = "1900-01-01";

function toBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["1", "true", "s", "sim"].includes(value.trim().toLowerCase());
  }
  return false;
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function hasValidDate(value) {
  if (!value) return false;
  return !String(value).startsWith(DATA_NULA_SONAX);
}

/**
 * Retorna quais das 3 primeiras parcelas (ordenadas por vencimento) estão em atraso.
 * Ex.: [1, 3] => 1ª e 3ª parcelas vencidas.
 */
export function getParcelasEmAtraso(contrato) {
  if (!contrato) return [];
  return [1, 2, 3].filter((numero) =>
    toBoolean(contrato[`parcela_${numero}_vencida`]),
  );
}

/**
 * Classifica o contrato retornado por script_fluxo_vendas em uma etapa do fluxo.
 * Apenas as 3 primeiras parcelas definem adimplência/inadimplência, e prevalece
 * a parcela mais avançada em atraso (1ª e 3ª vencidas => Inad. 3ª parcela).
 * A ordem dos testes define a precedência (o primeiro match vence):
 * 1º Cancelado, 2º Adimplente, 3º Vendas Validadas,
 * 4º Inad. 3ª parcela, 5º Inad. 2ª parcela, 6º Inad. 1ª parcela.
 */
export function classificarEtapaContrato(contrato) {
  if (!contrato) return null;

  const contratoAtivo = toBoolean(contrato.contrato_ativo);
  const vendaAtiva =
    contrato.venda_ativa === undefined || contrato.venda_ativa === null
      ? true
      : toBoolean(contrato.venda_ativa);
  const cancelado =
    !contratoAtivo ||
    !vendaAtiva ||
    hasValidDate(contrato.data_cancelamento_contrato) ||
    hasValidDate(contrato.data_cancelamento_venda);
  const totalMensalidades = toNumber(contrato.quantidade_titulos);
  const parcelasEmAtraso = getParcelasEmAtraso(contrato);

  if (cancelado) {
    return ETAPAS_VENDAS.CANCELADO;
  }
  if (totalMensalidades > 0 && parcelasEmAtraso.length === 0) {
    return ETAPAS_VENDAS.ADIMPLENTES;
  }
  if (totalMensalidades === 0) {
    return ETAPAS_VENDAS.VENDAS_VALIDADAS;
  }
  if (parcelasEmAtraso.includes(3)) {
    return ETAPAS_VENDAS.INAD_3_PARCELA;
  }
  if (parcelasEmAtraso.includes(2)) {
    return ETAPAS_VENDAS.INAD_2_PARCELA;
  }
  if (parcelasEmAtraso.includes(1)) {
    return ETAPAS_VENDAS.INAD_1_PARCELA;
  }
  return null;
}

function normalizeContrato(contrato) {
  const stage = classificarEtapaContrato(contrato);
  const sellerCpf = String(contrato.cpf_vendedor || "").replace(/\D/g, "");

  return {
    ...contrato,
    id: `${contrato.id_contrato || contrato.contrato}-${sellerCpf}`,
    code: contrato.contrato,
    contractNumber: contrato.contrato,
    contractType: contrato.tipo_contrato,
    contractDate: contrato.data_contrato,
    saleDate: contrato.data_venda,
    cancellationDate: hasValidDate(contrato.data_cancelamento_contrato)
      ? contrato.data_cancelamento_contrato
      : hasValidDate(contrato.data_cancelamento_venda)
      ? contrato.data_cancelamento_venda
      : null,
    customerName: contrato.nome_cliente,
    document: contrato.documento_cliente,
    phone: contrato.telefone,
    email: contrato.email,
    sellerName: contrato.nome_vendedor,
    sellerCpf,
    company: contrato.empresa,
    totalInstallments: toNumber(contrato.quantidade_titulos),
    overdueInstallments: toNumber(contrato.quantidade_titulos_vencidos),
    lateInstallments: getParcelasEmAtraso(contrato),
    overdueAmount: toNumber(contrato.valor_total_vencido),
    stage,
  };
}

function getList(path, mapper, params) {
  return http
    .get(`${baseUrl}${path}`, {
      params,
      headers: authHeader(),
    })
    .then((response) => {
      const data = (response && response.data && (response.data.data || response.data)) || [];
      return Array.isArray(data) ? data.map(mapper) : [];
    });
}

export default {
  getColumns() {
    return getList("/etapas", normalizeColumn);
  },

  getContratos(params = {}) {
    return getList("/contratos", normalizeContrato, params);
  },

  getInstallments(idContrato) {
    return getList(`/contratos/${idContrato}/parcelas`, normalizeInstallment);
  },

  getAgreements(idContrato) {
    return getList(`/contratos/${idContrato}/acordos`, normalizeAgreement);
  },

  getClaims(idContrato) {
    return getList(`/contratos/${idContrato}/sinistros`, normalizeClaim);
  },
};
